import { Component, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { DashService } from '@modules/dash-board/dash.service';
import { WorkflowService } from '@services/api/workflow.service';
import { isPublicReportLocation } from '@services/helpers/public-report-route';
import { TokenService } from '@services/helpers/token.service';
import { LeadManagementService } from '@services/lm-service/lead-management.service';
import { ActivityTrackerService } from '@services/user-activity/activity-tracker.service';
import { WS } from '@services/util/WebSocket.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-full',
  templateUrl: './full.component.html',
  styleUrls: ['./full.component.scss'],
  // ConfirmationService is no longer used here, but descendant components that still inject it without
  // their own provider (e.g. my-listings add-properties) resolve it from this layout.
  providers: [MessageService, ConfirmationService],
})
export class FullComponent implements OnInit {
  isMobileDevice: boolean = false;
  /**
   * Whether to render the authenticated app shell (header, sidebar, AI FAB, walkthrough overlay).
   *
   * Seeded from `window.location` rather than defaulting to `true`: on a cold open of a public
   * `/report-view/:slug` link the field is read during the component's FIRST change detection,
   * and a `true` seed briefly mounted `<app-header>`, whose bootstrap calls 401 for an anonymous
   * visitor and hard-navigated the browser to `/#/login`.
   */
  isHeader: boolean = !isPublicReportLocation();
  private routerEventsSubscription: Subscription;

  // Dynamic-sync "Remmi may feel slow" banner (WS type: dynamic-sync-banner)
  showSyncBanner: boolean = false;
  syncBannerTitle: string = '';
  syncBannerMessage: string = '';
  private syncBannerDismissed: boolean = false;
  private syncBannerSubscription: Subscription;

  private resizeSubscription: Subscription;

  constructor(
    public ws: WS,
    public lmService: LeadManagementService,
    private messageService: MessageService,
    public _workflowService: WorkflowService,
    public router: Router,
    public dash: DashService,
    private tokenService: TokenService,
    private activityTracker: ActivityTrackerService
  ) {}

  ngOnInit() {
    this.resizeSubscription = this.dash.screenSize$.subscribe(size => {
      if (size.width < 768) {
        this.isMobileDevice = true;
      } else {
        this.isMobileDevice = false;
      }
    });

    // Listen for the dynamic-sync banner pushed over the existing WebSocket.
    // Subscribe before connecting so a replayed "show" (mid-sync reconnect) is not missed.
    this.syncBannerSubscription = this.ws.getDynamicSyncBanner().subscribe((msg: any) => this.handleSyncBanner(msg));

    this.applyShellVisibility();
    // FullComponent is NOT re-created when navigating between its children, so computing this once
    // in ngOnInit left the shell mounted when an agent navigated from inside the app to a shared
    // `/report-view/:slug` link. The header then kept polling authenticated endpoints over the
    // customer-facing page — and on a 401 sent the browser to `/#/login`.
    this.routerEventsSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.applyShellVisibility());

    // Only open the socket for a real, authenticated session. Keying this off a stored `uid` was
    // wrong: the public report PIN flow itself writes `uid` to localStorage, so every visitor who
    // had ever unlocked a report opened an authenticated socket on their next anonymous visit.
    if (this.isHeader && this.tokenService.isUserloggedIn()) {
      this.initWebSocket();
      this.activityTracker.start();
    }
  }

  private applyShellVisibility(): void {
    this.isHeader = !isPublicReportLocation(this.router.url);
    // Also here, not only in ngOnInit: a tab that opens on a public /report-view link and then
    // navigates into Remmi would otherwise never start tracking, leaving that user invisible on
    // the User Activity dashboard for the life of the tab. `start()` is a no-op once running.
    if (this.isHeader && this.tokenService.isUserloggedIn()) {
      this.activityTracker.start();
    }
  }

  ngOnDestroy() {
    if (this.resizeSubscription) {
      this.resizeSubscription?.unsubscribe();
    }
    if (this.syncBannerSubscription) {
      this.syncBannerSubscription?.unsubscribe();
    }
    if (this.routerEventsSubscription) {
      this.routerEventsSubscription?.unsubscribe();
    }
  }

  // Drive the banner off data.syncing (source of truth), per the sync-banner spec.
  private handleSyncBanner(msg: any) {
    if (msg?.type !== 'dynamic-sync-banner') {
      return;
    }
    if (msg?.data?.syncing) {
      // Keep it hidden if the user dismissed it for this sync cycle.
      if (!this.syncBannerDismissed) {
        this.syncBannerTitle = msg?.data?.banner?.title;
        this.syncBannerMessage = msg?.data?.banner?.message;
        this.showSyncBanner = true;
      }
    } else {
      // Last sync finished/failed — reset dismissal so the next sync shows again.
      this.syncBannerDismissed = false;
      this.showSyncBanner = false;
    }
  }

  // Closing hides it for the current sync only; a new sync brings it back.
  closeSyncBanner() {
    this.syncBannerDismissed = true;
    this.showSyncBanner = false;
  }

  async initWebSocket() {
    // `uid` is empty right after an in-app logout/login (wiped on logout, rewritten only once `/me`
    // answers), so fall back to the id in the token or the session is left without a socket.
    this.ws.connect(this.ws.resolveUserId(), this.messageService);
  }

  getClassForMainContainer() {
    return {
      'pad-s': this.lmService.sideNavToggle,
      'pl-0': !this.lmService.sideNavToggle && this.isMobileDevice,
      'pad-l': !this.lmService.sideNavToggle && !this.isMobileDevice,
    };
  }

  toggleClick(eve: boolean) {
    this.lmService.sideNavToggle = !eve;
    // if (!this.isMobileDevice) {
    // }
  }
}
